
<template>
  <SAIUserProfile
    :fields="fields"
    :company-name="profile.companyName"
    :user-name="$auth.user.name"
    :editable="editable"
    :disable="savingUser"
    :saving="savingUser"
    :hide-editable-button="!canEditProfile"
    @update:input="update"
    @update:editable="editable = $event"
    @cancel="cancel"
    @save="save"
    @close="$emit('close')"
  />
</template>

<script>
import { MOBILE_REGEX, NUMBERS, SAIUserProfile, doesValuePassRegex, getUserProfileFields } from 'ecx.saigon.ui.common';
import { mapState, mapActions, mapGetters } from 'vuex';
import { INITIAL_USER } from '../../utils/constants';
import { required } from 'vuelidate/lib/validators';
import { add } from 'lodash';

export default {
  name: 'UserProfileWidget',
  data() {
    return {
      profile: { ...INITIAL_USER.profile },
      contactDetails: { ...INITIAL_USER.contactDetails },
      editable: false
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      savingUser: (state) => state.user.savingUser
    }),
    ...mapGetters('user', ['theme', 'canEditProfile']),
    ...mapGetters('application', ['creditCheckComplete']),
    customFields: function() {
      return [
        {
          label: 'First name',
          values: [
            {
              key: 'firstName',
              value: this.profile.firstName,
              disabled: true,
              required: true,
              validation: (val)=>{
                return val && val.length > 0;
              },
              props: {
                placeholder: 'Enter your first name'
              }
            }
          ]
        },
        {
          label: 'Last name',
          values: [
            {
              key: 'lastName',
              value: this.profile.lastName,
              disabled: true,
              required: true,
              validation: (val)=>{
                return val && val.length > 0;
              },
              props: {
                placeholder: 'Enter your last name'
              }
            }
          ]
        },
        {
          label: 'Employer',
          values: [
            {
              key: 'companyName',
              value: this.profile.companyName,
              required: true,
              disabled: true,
              validation: ()=>{
                return {
                  required
                }
              },
              props: {
                placeholder: 'Enter your company name'
              }
            }
          ]
        },
        {
          label: 'Unit/Level',
          values: [
            {
              key: 'unitNumber',
              value: this.profile.unitNumber,
              validation: (value) => true,
              required: false,
              props: {
                placeholder: 'Enter your unit/level number'
              }
            }
          ]
        },
        {
          label: 'Address',
          values: [
            {
              key: 'addressLineOne',
              value: this.profile.addressLineOne,
              required: true,
              validation: (value) => {                
                return value && value.length > 0;
              },
              props: {
                placeholder: 'Enter your address'
              }
            }
          ]
        },
        {
          label: 'Suburb',
          values: [
            {
              key: 'addressLineTwo',
              value: this.profile.addressLineTwo,
              required: true,
              validation: (value) => {
                return value && value.length > 0;
              },
              props: {
                placeholder: 'Enter your suburb'
              }
            }
          ]
        },
        {
          label: 'Postcode',
          values: [
            {
              key: 'postcode',
              value: this.profile.postcode,
              required: true,
              validation: (value) => value && doesValuePassRegex(value, NUMBERS),
              props: {
                placeholder: 'Enter your postcode'
              }
            }
          ]
        },
        {
          label: 'Work phone',
          values: [
            {
              key: 'workPhone',
              value: this.profile.workPhone,
              required: true,
              validation: (value) => !value || doesValuePassRegex(value, MOBILE_REGEX.au),
              props: {
                placeholder: 'Enter your work phone number'
              }
            }
          ]
        },
      ]
    },
    fields() {
      const email = this.$auth.user.email;
      let fields = getUserProfileFields(
        {
          email,
          ...this.profile
        },
        email === this.user.profile.companyEmail,
        this.user.features.novatedPayrollCostCenterEmployee,
        this.creditCheckComplete
      );

      let contact = this.theme().novatedBrand?.support?.email;
      fields[0].values[0].locked = `Contact us at ${contact} to update your login email`;
      fields = fields.filter((field) => field.label !== 'Postcode');
      
      return [...fields, ...this.customFields];
    }
  },
  methods: {
    ...mapActions('user', ['saveUserProfile']),
    update({ key, value }) {
      this.profile = {
        ...this.profile,
        [key]: value || ''
      };
    },
    cancel() {
      this.resetProfile();
      this.editable = false;
    },
    async save() {
      await this.saveUserProfile(this.profile);
      this.editable = false;
      this.$emit('close');
    },
    resetProfile() {
      this.profile = {
        ...this.user.profile, 
        addressLineOne: this.user.contactDetails?.addressLineOne,
        addressLineTwo: this.user.contactDetails?.addressLineTwo,
        stateCode: this.user.contactDetails?.stateCode,
        unitNumber: this.user.contactDetails?.unitNumber,
        workPhone: this.user.contactDetails?.workPhone,
      } || { ...INITIAL_USER.profile };
    }
  },
  mounted() {
    this.resetProfile();
  },
  components: {
    SAIUserProfile
  }
};
</script>
